/**
 * Service de capture des paramètres UTM pour l'attribution marketing.
 * Capture au premier chargement de la session, persiste dans sessionStorage.
 * Conditionné au consentement RGPD (analytique).
 *
 * Stratégie anti-perte :
 * - bufferUtm() sauvegarde les params UTM en mémoire JS dès le mount (sans consentement)
 * - captureUtm() écrit en sessionStorage uniquement quand le consentement est donné
 * - Si l'URL a changé (navigation SPA), captureUtm() utilise le buffer mémoire
 */
import { useConsentStore } from '../stores/useConsentStore'

const UTM_STORAGE_KEY = 'bonurba_utm'

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']

/** Buffer mémoire — pas de stockage persistant, donc pas de donnée personnelle RGPD */
let _pendingUtm = null

/**
 * Lire les paramètres UTM depuis l'URL courante et les garder en mémoire.
 * À appeler inconditionnellement au mount de l'app (avant le consentement).
 * Ne touche à aucun stockage persistant.
 */
export function bufferUtm() {
  if (_pendingUtm) return // déjà bufferisé
  const params = new URLSearchParams(window.location.search)
  const hasUtm = UTM_PARAMS.some((k) => params.has(k))
  if (!hasUtm) return

  const utm = {}
  for (const key of UTM_PARAMS) {
    const value = params.get(key)
    if (value) utm[key] = value
  }
  _pendingUtm = {
    ...utm,
    referrer: document.referrer || null,
    first_landing_url: window.location.pathname + window.location.search,
    landing_at: new Date().toISOString(),
  }
}

/**
 * Capturer les paramètres UTM : écrire en sessionStorage.
 * Utilise le buffer mémoire si l'URL a déjà changé (consentement tardif).
 * Ne remplace PAS les données déjà capturées dans cette session.
 * @returns {object|null} Les données UTM capturées, ou null si déjà présentes / pas de consentement.
 */
export function captureUtm() {
  if (!useConsentStore.getState().analytique) return null
  if (sessionStorage.getItem(UTM_STORAGE_KEY)) return null

  // Source prioritaire : buffer mémoire (survit à la navigation SPA)
  // Fallback : URL courante (cas où bufferUtm n'a pas été appelé)
  let data = _pendingUtm
  if (!data) {
    const params = new URLSearchParams(window.location.search)
    const hasUtm = UTM_PARAMS.some((k) => params.has(k))
    if (!hasUtm) return null

    const utm = {}
    for (const key of UTM_PARAMS) {
      const value = params.get(key)
      if (value) utm[key] = value
    }
    data = {
      ...utm,
      referrer: document.referrer || null,
      first_landing_url: window.location.pathname + window.location.search,
      landing_at: new Date().toISOString(),
    }
  }

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data))
  _pendingUtm = null // consommé
  return data
}

/**
 * Récupérer les données UTM capturées pour cette session.
 * @returns {object|null}
 */
export function getUtmData() {
  const raw = sessionStorage.getItem(UTM_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}
